import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/Dialog';
import { type FragmentType, useFragment } from '@/gql/codegen';
import { graphql } from '@/gql/codegen/gql';
import { Button } from '@/routes/_embaUserLayout/-components/Button';
import { useInputTextValidator } from '@/routes/_embaUserLayout/-hooks/useInputTextValidator';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import type React from 'react';
import { useEffect } from 'react';
import styles from './index.module.scss';

export const COURSE_LESSON_SELF_SUMMARIZING_QUESTION = graphql(/* GraphQL */ `
  fragment EmbaCourseLessonSelfSummarizingQuestion on CourseLessonSelfSummarizingQuestion {
    id
    content
    note
    userAnswer {
      id
      content
    }
  }
`);

export type SelfSummarizingQuestionProps = {
  question: FragmentType<typeof COURSE_LESSON_SELF_SUMMARIZING_QUESTION>;
  isAlreadyAnswered: boolean;
  answer: string;
  setAnswer: (value: string) => void;
  handleAnswerSelfSummarizingQuestion: () => void;
  handleNext: () => void;
  isLastQuestion: boolean;
  allAnswered: boolean;
  isAnsweredModalOpen: boolean;
  closeAnsweredModal: () => void;
  answerSelfSummarizingQuestionLoading: boolean;
};

export const SelfSummarizingQuestion: React.FC<
  SelfSummarizingQuestionProps
> = ({
  question,
  isAlreadyAnswered,
  answer,
  setAnswer,
  handleAnswerSelfSummarizingQuestion,
  handleNext,
  isLastQuestion,
  allAnswered,
  isAnsweredModalOpen,
  closeAnsweredModal,
  answerSelfSummarizingQuestionLoading,
}) => {
  const questionData = useFragment(
    COURSE_LESSON_SELF_SUMMARIZING_QUESTION,
    question,
  );

  const { valid, isValid } = useInputTextValidator(answer);
  useEffect(() => {
    valid();
  }, [valid]);

  return (
    <>
      <textarea
        value={questionData.userAnswer?.content ?? answer}
        onChange={(e) => setAnswer(e.target.value)}
        disabled={isAlreadyAnswered}
        className={styles.selfSummarizingQuestionForm}
        maxLength={1000}
      />
      <div className={styles.selfSummarizingQuestionFormCount}>
        <span>
          本設問の回答は受講管理者に共有されます。送信後の修正はできません。
        </span>
        {!isAlreadyAnswered && <span>(1000文字以内)</span>}
      </div>

      {questionData.note && (
        <p className={styles.selfSummarizingQuestionNote}>
          {questionData.note}
        </p>
      )}

      <div className={styles.answerButton}>
        {!questionData.userAnswer ? (
          <Button
            onClick={() => {
              handleAnswerSelfSummarizingQuestion();
            }}
            disabled={!isValid}
            // Loadingが終わってからanswer取得するまでに時間がかかるため、isAnsweredModalOpenも利用
            loading={
              answerSelfSummarizingQuestionLoading || isAnsweredModalOpen
            }
          >
            決定
          </Button>
        ) : (
          <Button
            onClick={() => {
              handleNext();
            }}
          >
            {isLastQuestion && allAnswered ? '終了' : '次の問題へ'}
          </Button>
        )}
      </div>
      <Dialog
        open={isAnsweredModalOpen && isAlreadyAnswered}
        onOpenChange={closeAnsweredModal}
      >
        <VisuallyHidden>
          <DialogDescription>
            自分の言葉で体系化回答後のモーダル
          </DialogDescription>
        </VisuallyHidden>

        <DialogContent>
          <div className={styles.AnsweredDialog}>
            {questionData.userAnswer && (
              <>
                <DialogTitle className={styles.AnsweredDialogTitle}>
                  記入が完了しました！
                  <br />
                  お疲れさまでした！
                </DialogTitle>
                <Button
                  onClick={() => {
                    handleNext();
                  }}
                >
                  {isLastQuestion && allAnswered ? '終了' : '次の問題へ'}
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
